export interface CurrentLocation {
  city?: string
  state?: string
  postalCode?: string
  country?: string
  lat?: number
  lng?: number
  timezone?: string
  terminalId?: string
  trailerId?: string
  summary?: string
  sequence?: number
  eventDateTime?: TrackDateTime
  eventDate?: string
  eventTime?: string
}

export const trackStatuses = [
  'pending',
  'enroute-to-pickup',
  'in-transit',
  'out-for-delivery',
  'delivered',
  'not-found',
  'error',
] as const

export type TrackingStatus = (typeof trackStatuses)[number]

export interface BaseTrackDetail {
  autoTrack?: boolean
  currentLocation?: CurrentLocation
  currentStatus: TrackingStatus
  deliveryDateActual?: Date
  deliveryDateEstimate?: Date
  error?: string
  errorType?: 'not-found' | 'external' | 'internal' | 'unknown'
  pickupDateActual?: Date
  trackEvents?: TrackEvent[]
  trackingNumber?: string
  estimatedDeliveryTimeWindow?: EstimatedDeliveryTimeWindow
  deliveryDetails?: DeliveryDetails
  interlineDetails?: InterlineDetails
  assetCarrierTrackingNumber?: string
}

export interface InterlineDetails {
  carrierName?: string
  carrierCode: string
  pro: string
}

export const v1TrackEventTypes = [
  'pickup',
  'in-transit',
  'arrived',
  'departed',
  'delivered',
  'delayed',
  'out-for-delivery',
  'update',
  'stop-completed',
  'stop-incomplete',
] as const
// TODO - may not need to keep v1 event types around much long after
// we move forward with v2 tracking.  But while early in dev this is helpful
export const v2TrackEventTypes = [
  'origin-arrived',
  'picked-up',
  'in-transit',
  'terminal-arrived',
  'terminal-departed',
  'terminal-unloaded',
  'terminal-loaded',
  'terminal-other',
  'out-for-delivery',
  'destination-arrived',
  'destination-unloaded',
  'delivered',
  'cartage-handoff',
  'interline-handoff',
  'other',
  'exception',
  'information',
  'update',
  'stop-completed',
  'stop-incomplete',
] as const
export const trackEventTypes = [
  ...v1TrackEventTypes,
  ...v2TrackEventTypes,
] as const

export type TrackEventTypeV1 = (typeof v1TrackEventTypes)[number]
export type TrackEventTypeV2 = (typeof v2TrackEventTypes)[number]
export type TrackEventType = (typeof trackEventTypes)[number]

export interface TrackEvent {
  //loc
  city?: string
  state?: string
  lat?: number
  lng?: number
  postalCode?: string
  country?: string

  // dates
  createdDate: Date
  eventDate?: string
  eventTime?: string
  eventDateTime?: TrackDateTime

  // enums
  eventMode: 'manual' | 'api'
  eventSource: 'shipper' | 'carrier'
  eventType: TrackEventType
  status?: TrackingStatus

  //other
  sequence?: number
  summary?: string
  terminalId?: string
  timezone?: string
  trailerId?: string
  exceptionDescription?: string
  exceptionCode?: ExceptionCode
}

export type TrackDateTime = {
  date: string
  time?: string
  isUTC?: boolean
}
export interface TimeWindow {
  begins: TrackDateTime
  ends: TrackDateTime
}

export interface EstimatedDeliveryTimeWindow extends TimeWindow {}

export interface DeliveryAppointment extends TimeWindow {}
export interface DeliveryDetails {
  appointment?: DeliveryAppointment
  stopsAway?: number
}
export interface TrackDetail extends BaseTrackDetail {
  autoTrack?: boolean
  items?: ItemTrackDetail[]
  lastUpdatedDate?: Date
}

export type ItemTrackDetail = BaseTrackDetail & {
  itemId: string
  unitIndex: number
}

export const exceptionCodes = [
  'awaiting-customs-clearance',
  'canceled',
  'consolidation-hold',
  'damaged',
  'delayed',
  'delivery-appointment-needed',
  'delivery-re-attempt-tomorrow',
  'delivery-unsuccessful',
  'freight-refused',
  'freight-refused-damaged',
  'freight-refused-short',
  'freight-unavailable',
  'lost',
  'no-receiving-personnel',
  'on-hand',
  'other',
  'overage',
  'reconsigned',
  'returned-to-dock',
  'returned-to-shipper',
  'service-exemption',
  'short',
  'special-equipment-required',
  'terminal-pickup',
  'unable-to-locate-consignee',
  'weight-correction',
] as const

export type ExceptionCode = (typeof exceptionCodes)[number]
